// Chain + bail math and geometry. Everything above "THREE geometry" is pure
// (no DOM, no Lume) so it can be unit tested in node and reused by the
// mannequin card: `chainPath(points)` turns ANY polyline into link placements.
//
// Scene units: y points DOWN (Lume), pendant is at the bottom of the necklace.
import {CatmullRomCurve3, Vector3, TubeGeometry} from 'three'

/**
 * Link / ring dimensions in scene units (pendant is up to 150 units wide, so
 * these are scaled to GoldHill's look: wire ~1/8 of a letter stroke).
 */
export const DIMS = {
  /** link tube radius (wire is 2x this) */
  linkWire: 0.5,
  /** link outer length (long axis) */
  linkLength: 6.6,
  /** link outer width (short axis) */
  linkWidth: 5.0,
  /** bail ring: centerline radius and tube radius */
  ringRadius: 3.4,
  ringWire: 0.7,
}

/** Distance between neighbouring link centres so each link's end passes through the next (wires touching). */
export const LINK_PITCH = 2 * (DIMS.linkLength / 2 - DIMS.linkWire) - 2 * DIMS.linkWire

/** @typedef {[number, number, number]} V3 */
/**
 * @typedef {object} LinkPlacement
 * @property {V3} p link centre
 * @property {V3} t unit tangent (long axis of the link)
 * @property {V3} n unit normal of the link's plane (alternates ~90deg between neighbours)
 * @property {number} i index along the chain
 */

/** @param {V3} a @param {V3} b @returns {number} */
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
/** @param {V3} a @param {V3} b @returns {V3} */
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
/** @param {V3} a @returns {V3} */
const norm = a => {
  const l = Math.hypot(...a) || 1
  return [a[0] / l, a[1] / l, a[2] / l]
}

/**
 * Smooth curve through control points (centripetal Catmull-Rom).
 * @param {V3[]} points at least 2 points
 * @returns {CatmullRomCurve3}
 */
function curveThrough(points) {
  const c = new CatmullRomCurve3(
    points.map(p => new Vector3(...p)),
    false,
    'centripetal',
  )
  c.arcLengthDivisions = 2000
  return c
}

/**
 * PURE: place interlocked links along a polyline. The FIRST link's end wire sits exactly on points[0]
 * and the LAST link's end wire exactly on the final point (that is where the bail rings are). Link
 * count is odd so both end links share an orientation; neighbours alternate between two planes
 * 90deg apart (like a real cable chain).
 * @param {V3[]} points control points of the chain centreline (smoothed through, ends included)
 * @param {{pitch?: number, reference?: V3, firstEdgeOn?: boolean}} [opts] `reference`: direction used to
 *   pick the "face-on" plane (default +Z, toward the viewer). With `firstEdgeOn` (default true) link 0
 *   is edge-on to the reference, i.e. it threads through a ring lying in the plane facing the reference.
 * @returns {LinkPlacement[]}
 */
export function chainPath(points, opts = {}) {
  if (points.length < 2) return []
  const pitch = opts.pitch ?? LINK_PITCH
  const ref = opts.reference ?? [0, 0, 1]
  const firstEdgeOn = opts.firstEdgeOn ?? true
  const half = DIMS.linkLength / 2 - DIMS.linkWire // centreline half-length of a link
  const curve = curveThrough(points)
  const total = curve.getLength()
  const span = total - 2 * half
  if (span <= 0) return []
  let n = Math.round(span / pitch) + 1
  if (n % 2 === 0) n += 1
  if (n < 3) n = 3
  const step = span / (n - 1)
  /** @type {LinkPlacement[]} */
  const out = []
  for (let i = 0; i < n; i++) {
    const u = Math.min(1, (half + i * step) / total)
    const p = curve.getPointAt(u)
    const t0 = curve.getTangentAt(u)
    /** @type {V3} */
    const t = norm([t0.x, t0.y, t0.z])
    const d = dot(ref, t)
    /** @type {V3} */
    let nf = norm([ref[0] - d * t[0], ref[1] - d * t[1], ref[2] - d * t[2]])
    if (Math.abs(d) > 0.999) nf = norm(cross(t, [0, 1, 0]))
    const ne = norm(cross(t, nf)) // edge-on normal (lies in the path plane for planar paths)
    const faceOn = (i % 2 === 0) !== firstEdgeOn
    out.push({p: [p.x, p.y, p.z], t, n: faceOn ? nf : ne, i})
  }
  return out
}

/**
 * Total length of the polyline's smoothed curve.
 * @param {V3[]} points
 * @returns {number}
 */
export function pathLength(points) {
  return points.length < 2 ? 0 : curveThrough(points).getLength()
}

// ---------------------------------------------------------------- bails

/**
 * @typedef {object} Bail
 * @property {number} x ring centre x (pendant-local, y-down, origin = shape centre)
 * @property {number} y ring centre y
 * @property {boolean} onSolid true when the ring's lower wire is anchored in solid pendant material
 */

/**
 * Rasterize the solid area of the shapes (even-odd across every outline, so holes in o/e/a/8 are respected).
 * @param {import('three').Shape[]} shapes
 * @param {number} cell grid cell size in scene units
 */
function rasterize(shapes, cell) {
  /** @type {import('three').Vector2[][]} */
  const loops = []
  for (const s of shapes) {
    const f = s.extractPoints(8)
    loops.push(f.shape)
    for (const h of f.holes) loops.push(h)
  }
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const l of loops)
    for (const p of l) {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    }
  const cols = Math.max(1, Math.ceil((maxX - minX) / cell))
  const rows = Math.max(1, Math.ceil((maxY - minY) / cell))
  const solid = new Uint8Array(cols * rows)
  for (let r = 0; r < rows; r++) {
    const y = minY + (r + 0.5) * cell
    /** @type {number[]} */
    const xs = []
    for (const l of loops) {
      for (let i = 0, j = l.length - 1; i < l.length; j = i++) {
        const a = l[j],
          b = l[i]
        if (a.y > y !== b.y > y) xs.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x))
      }
    }
    xs.sort((a, b) => a - b)
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const c0 = Math.max(0, Math.ceil((xs[k] - minX) / cell - 0.5))
      const c1 = Math.min(cols - 1, Math.floor((xs[k + 1] - minX) / cell - 0.5))
      for (let c = c0; c <= c1; c++) solid[r * cols + c] = 1
    }
  }
  return {minX, minY, maxX, maxY, cols, rows, cell, solid}
}

/**
 * PURE: where the two bail rings go. Each ring's lower wire is anchored on SOLID pendant material
 * (disc of radius ~wire fully solid) nearest the top-left / top-right corner of the bounds, so
 * thin letters, narrow/wide text, and holes/odd SVG outlines all work. If both candidates collapse
 * (a single thin glyph) the rings are pushed apart until they just clear each other.
 * @param {import('three').Shape[]} shapes normalized pendant shapes (y-down, centred)
 * @param {{ringRadius?: number, ringWire?: number}} [opts]
 * @returns {{left: Bail, right: Bail, bounds: {minX: number, minY: number, maxX: number, maxY: number}}}
 */
export function bailPositions(shapes, opts = {}) {
  const R = opts.ringRadius ?? DIMS.ringRadius
  const rw = opts.ringWire ?? DIMS.ringWire
  const cell = 0.5
  const g = rasterize(shapes, cell)
  /** @param {number} c @param {number} r */
  const isSolid = (c, r) => c >= 0 && r >= 0 && c < g.cols && r < g.rows && g.solid[r * g.cols + c] === 1
  /** @param {number} c @param {number} r @param {number} m */
  const solidDisc = (c, r, m) => {
    for (let dy = -m; dy <= m; dy++)
      for (let dx = -m; dx <= m; dx++) if (dx * dx + dy * dy <= m * m && !isSolid(c + dx, r + dy)) return false
    return true
  }
  /**
   * Solid cell nearest (cornerX, top) whose m-cell disc is solid; `side` = +1 requires x >= limit.
   * @param {number} cornerX @param {number | null} limit @param {number} side @param {number} m
   * @returns {{x: number, y: number} | null}
   */
  const nearest = (cornerX, limit, side, m) => {
    /** @type {{x: number, y: number} | null} */
    let best = null
    let bestD = Infinity
    for (let r = 0; r < g.rows; r++) {
      const y = g.minY + (r + 0.5) * cell
      for (let c = 0; c < g.cols; c++) {
        if (!g.solid[r * g.cols + c]) continue
        const x = g.minX + (c + 0.5) * cell
        if (limit !== null && (x - limit) * side < 0) continue
        const d = Math.hypot(x - cornerX, (y - g.minY) * 1.4)
        if (d < bestD && solidDisc(c, r, m)) {
          bestD = d
          best = {x, y}
        }
      }
    }
    return best
  }
  const margin = Math.max(1, Math.round((rw + 0.1) / cell))
  /** @param {number} x @param {number | null} limit @param {number} side */
  const pick = (x, limit, side) =>
    nearest(x, limit, side, margin) ?? nearest(x, limit, side, 1) ?? nearest(x, limit, side, 0) ?? {x, y: g.minY}
  let pl = pick(g.minX, null, 0)
  let pr = pick(g.maxX, null, 0)
  const minSep = 2 * (R + rw) + 1
  let onSolidL = true
  let onSolidR = true
  if (pr.x - pl.x < minSep) {
    // narrow glyph: re-pick the right ring to the right of the left one; else push apart symmetrically
    const alt = pick(g.maxX, pl.x + minSep, 1)
    if (alt.x - pl.x >= minSep - 1e-6) pr = alt
    else {
      const mid = (pl.x + pr.x) / 2
      const y = Math.min(pl.y, pr.y)
      pl = {x: mid - minSep / 2, y}
      pr = {x: mid + minSep / 2, y}
      /** @param {number} x @param {number} y0 */
      const probe = (x, y0) => isSolid(Math.floor((x - g.minX) / cell), Math.floor((y0 - g.minY) / cell))
      onSolidL = probe(pl.x, pl.y)
      onSolidR = probe(pr.x, pr.y)
    }
  }
  // ring's lower wire is centred on the anchor point; the ring rises above the pendant edge
  return {
    left: {x: pl.x, y: pl.y - R, onSolid: onSolidL},
    right: {x: pr.x, y: pr.y - R, onSolid: onSolidR},
    bounds: {minX: g.minX, minY: g.minY, maxX: g.maxX, maxY: g.maxY},
  }
}

/**
 * PURE: where the chain's end-link wire touches a ring: just below the ring's top inner edge, so the
 * ring's top wire passes through the link hole and the link wire passes through the ring hole.
 * @param {{x: number, y: number}} ring ring centre
 * @returns {V3}
 */
export function ringThreadPoint(ring) {
  return [ring.x, ring.y - DIMS.ringRadius + DIMS.ringWire + DIMS.linkWire, 0]
}

/**
 * PURE: centreline of the necklace between the two ring thread points: a V rising from the rings that
 * blends into a rounded neck loop, scaled by bisection so the path length equals `inches` of chain.
 * Feed the result to `chainPath()`. (The mannequin card can instead build its own points.)
 * @param {number} inches chain length
 * @param {{x: number, y: number}} left left ring centre (scene coords)
 * @param {{x: number, y: number}} right right ring centre
 * @param {number} unitsPerInch scene units per inch of chain
 * @returns {V3[]}
 */
export function necklacePoints(inches, left, right, unitsPerInch) {
  const a0 = ringThreadPoint(left)
  const b0 = ringThreadPoint(right)
  const len = inches * unitsPerInch
  const d = (b0[0] - a0[0]) / 2
  const cx = (a0[0] + b0[0]) / 2
  const ringY = Math.min(a0[1], b0[1])
  /** @param {number} ry @returns {V3[]} */
  const build = ry => {
    const rx = Math.max(ry * 0.86, Math.min(d * 1.25 + 8, len * 0.3)) // wide pendants widen the neck loop instead of folding the chain under it
    const x1 = Math.min(Math.max(0.72 * rx, d + 12), 0.95 * rx)
    const phi1 = Math.asin(Math.min(1, x1 / rx))
    const y0 = ringY - (x1 - d) * 1.7 + ry - ry * Math.cos(phi1) // bottom of the ellipse
    /** @type {V3[]} */
    const pts = [a0, [a0[0], a0[1] - 4, 0]]
    const N = 24
    for (let k = 0; k <= N; k++) {
      const phi = -phi1 - (k / N) * (2 * Math.PI - 2 * phi1) // up the left side, over the top, down the right
      pts.push([cx + rx * Math.sin(phi), y0 - ry + ry * Math.cos(phi), 0])
    }
    pts.push([b0[0], b0[1] - 4, 0], b0)
    return pts
  }
  let lo = 1
  let hi = len
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    if (pathLength(build(mid)) < len) lo = mid
    else hi = mid
  }
  return build((lo + hi) / 2)
}

// ---------------------------------------------------------------- THREE geometry

/**
 * Closed flattened-oval ("stadium") centreline in the XY plane, long axis along +X.
 * @returns {CatmullRomCurve3}
 */
function ovalCurve() {
  const half = DIMS.linkLength / 2 - DIMS.linkWire
  const rad = DIMS.linkWidth / 2 - DIMS.linkWire
  const straight = Math.max(0, half - rad)
  /** @type {Vector3[]} */
  const pts = []
  const n = 10
  for (let k = 0; k <= n; k++) {
    const a = -Math.PI / 2 + (k / n) * Math.PI
    pts.push(new Vector3(straight + Math.cos(a) * rad, Math.sin(a) * rad, 0))
  }
  for (let k = 0; k <= n; k++) {
    const a = Math.PI / 2 + (k / n) * Math.PI
    pts.push(new Vector3(-straight + Math.cos(a) * rad, Math.sin(a) * rad, 0))
  }
  return new CatmullRomCurve3(pts, true, 'centripetal')
}

/**
 * One cable link: thin tube along a flattened oval (long axis X, plane XY).
 * @returns {import('three').BufferGeometry}
 */
export function linkGeometry() {
  return new TubeGeometry(ovalCurve(), 64, DIMS.linkWire, 8, true)
}

/**
 * Round jump ring in the XY plane.
 * @returns {import('three').BufferGeometry}
 */
export function ringGeometry() {
  /** @type {Vector3[]} */
  const pts = []
  const n = 24
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    pts.push(new Vector3(Math.cos(a) * DIMS.ringRadius, Math.sin(a) * DIMS.ringRadius, 0))
  }
  return new TubeGeometry(new CatmullRomCurve3(pts, true, 'centripetal'), 36, DIMS.ringWire, 8, true)
}
